import React from 'react'

import cx from 'classnames'

import { AccountActivityAccountAvatar, AccountActivityUserAvatar } from 'components/Avatars'
import { OrgUsersById } from 'context/orgUsersContext'
import { ActivityGroupByEndUser, CompanyData } from 'modules/accountActivity/domain'
import { userActivityTypeToPreviewSnippet } from 'utils/activity'
import { timeSinceUpdated } from 'utils/timeStamp'

import styles from './styles.module.css'

interface AvatarProps {
  imageUrl: string
  inCrm?: boolean
}

interface ToolTipData {
  Avatar: any
  avatarProps: AvatarProps
  avatarLabel: string | undefined
  subheader: string | React.ReactElement | undefined
  activityInfo: React.ReactElement | undefined
}

// Util functions
const isCompany = (x: ActivityGroupByEndUser | CompanyData): x is CompanyData =>
  (x as CompanyData).domain !== undefined

const isContact = (x: ActivityGroupByEndUser | CompanyData): x is ActivityGroupByEndUser =>
  (x as ActivityGroupByEndUser).endUserData !== undefined

/**
 * @description - This functions returns a normalized data structure for the purpose of rendering
 * tooltip activity data for both Contacts and CompanyData.
 *
 * @param data - {ActivityGroupByEndUser | CompanyData}
 * @param orgUsersById - {OrgUsersById}
 *
 * @returns {ToolTipData | undefined}
 */
const getToolTipData = (
  data: ActivityGroupByEndUser | CompanyData,
  orgUsersById: OrgUsersById
): ToolTipData | undefined => {
  if (isCompany(data)) {
    const { name, domain, industry, employeeCountRange, city, state, url } = data
    return {
      Avatar: AccountActivityAccountAvatar,
      avatarProps: {
        imageUrl: data.logo || ''
      },
      avatarLabel: name || domain,
      subheader: (
        <div className={styles.CompanyDetails}>
          <div className={styles.SubHeader}>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          </div>

          {industry && (
            <div className={cx(styles.SubHeader, styles.TwoLineTruncated)}>
              {industry}{' '}
              {employeeCountRange && (
                <span className={styles.SubHeader}> · {employeeCountRange} employees</span>
              )}
              {city && state && (
                <span className={styles.SubHeader}>
                  {' '}
                  · {city}, {state}
                </span>
              )}
            </div>
          )}
        </div>
      ),
      activityInfo: undefined
    }
  } else if (isContact(data)) {
    const {
      activityList,
      name,
      email,
      role,
      endUserData: { locationCity, locationState }
    } = data
    const { action, preview} = userActivityTypeToPreviewSnippet(activityList[0], orgUsersById)
    return {
      Avatar: AccountActivityUserAvatar,
      avatarProps: {
        imageUrl: data.avatarUrl || ''
      },
      avatarLabel: name || email || 'Site Visitor',
      subheader: (
        <div className={styles.SubHeader}>
          {`${role ? `${role} · ` : ''}${locationCity && locationState ? `${locationCity}, ${locationState}` : ''
            }`}
        </div>
      ),
      activityInfo: data.activityList?.length ? (
        <>
          <span className={styles.ActivityInfoEvent}>
            <span className={styles.ActivityInfo}>
              Last activity: {action} {preview}
            </span>
          </span>
          <div className={styles.ActivityTime}>{`${timeSinceUpdated(
            activityList[0].createdAt
          )} ago`}</div>
        </>
      ) : (
        undefined
      )
    }
  }
}

export default getToolTipData
